import {
  AlertTriangle,
  CheckCircle2,
  Filter,
  Info,
  Menu,
  RefreshCw,
  Settings,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useDetectConflictsMutation } from '~entities/recital-manager/api/detect-conflicts';
import { useConflictsQuery } from '~entities/recital-manager/api/get-conflicts';
import { useResolveConflictMutation } from '~entities/recital-manager/api/resolve-conflict';
import { ConflictResolutionDrawer } from '~entities/recital-manager/ui/conflict-resolution-drawer';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { Alert, AlertDescription } from '~/shared/shadcn/ui/alert';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/shared/shadcn/ui/sheet';

import { EditConflictRulesDialog } from './edit-conflict-rules-dialog';

interface ConflictsTabProps {
  recitalId: string;
}

type FilterOption = 'all' | 'unresolved' | 'resolved';
type SeverityFilter = 'all' | 'error' | 'warning';

import type {
  ConflictType,
  PerformanceConflict,
  PopulatedPerformanceRef,
  PopulatedStudentRef,
} from '~entities/recital-manager/model/recital';

const getPerformanceName = (
  ref: string | PopulatedPerformanceRef | null,
): string => {
  if (!ref) return '';
  if (typeof ref === 'string') return ref.slice(-8);
  return ref.name ?? ref._id.slice(-8);
};

const getStudentNames = (conflict: PerformanceConflict): string => {
  const ids = (conflict.relatedStudentIds || []).filter(
    (s): s is string | PopulatedStudentRef => s != null,
  );

  const fromIds = ids
    .map((s) => {
      if (typeof s === 'string') return null;
      const name = `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim();
      return name || null;
    })
    .filter((name): name is string => Boolean(name));

  if (fromIds.length > 0) return fromIds.join(', ');

  if (conflict.metadata?.studentNames?.length) {
    return conflict.metadata.studentNames.join(', ');
  }
  if (conflict.metadata?.studentName) {
    return conflict.metadata.studentName;
  }

  // Orphaned student IDs that failed populate — still show something actionable
  if (ids.length > 0) {
    return ids
      .map((s) => (typeof s === 'string' ? `Student …${s.slice(-6)}` : null))
      .filter(Boolean)
      .join(', ') || 'Unknown student';
  }

  return 'No students';
};

const formatGap = (count: number): string =>
  `${count} ${count === 1 ? 'performance' : 'performances'}`;

const CONFLICT_LABELS: Record<ConflictType, string> = {
  insufficient_gap: 'Insufficient Gap',
  student_overload: 'Student Overload',
  missing_required_data: 'Missing Required Data',
  time_overlap: 'Time Overlap',
  age_data_issue: 'Age Data Issue',
  sibling_in_different_shows: 'Siblings in Different Shows',
};

function getResolutionGuidance(conflict: PerformanceConflict): string | null {
  const m = conflict.metadata;
  switch (conflict.conflictType) {
    case 'insufficient_gap':
      if (m?.actualGap === 0)
        return 'These performances are back-to-back with no gap. Move one to a later slot or add an act between them to allow costume change time.';
      if (m?.requiredGap)
        return `Increase the gap between these performances to at least ${formatGap(m.requiredGap)}, or adjust the conflict rules if this gap is acceptable.`;
      return null;
    case 'student_overload':
      return `This student has ${m?.performanceCount ?? 'many'} performances in one show. Consider redistributing across shows to reduce fatigue.`;
    case 'time_overlap':
      return 'This student is scheduled in multiple shows at the same time. Move one performance to a different show or time slot.';
    case 'missing_required_data':
      return `The "${m?.missingField ?? 'required'}" field is missing. Update the performance details before publishing.`;
    case 'age_data_issue':
      if (m?.issue === 'missing_dob')
        return `Update ${m?.studentName ?? "this student"}'s date of birth in their profile so age-based rules can be applied.`;
      return `Review ${m?.studentName ?? "this student"}'s age data — it may be missing or invalid.`;
    case 'sibling_in_different_shows':
      return `${m?.siblingCount ?? 'Multiple'} siblings are split across different shows, which may be inconvenient for the family. Consider moving them to the same show if possible.`;
    default:
      return null;
  }
}

export const ConflictsTab = ({ recitalId }: ConflictsTabProps) => {
  const [resolvedFilter, setResolvedFilter] =
    useState<FilterOption>('unresolved');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [conflictActionsOpen, setConflictActionsOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (resolvedFilter !== 'unresolved') count++;
    if (severityFilter !== 'all') count++;
    return count;
  }, [resolvedFilter, severityFilter]);
  const { hasAdminAccess } = useCurrentUserRole();
  const { data: conflicts, isLoading } = useConflictsQuery({
    recitalId,
    filters: {
      resolved:
        resolvedFilter === 'all' ? undefined : resolvedFilter === 'resolved',
      severity: severityFilter === 'all' ? undefined : severityFilter,
    },
  });

  const detectConflictsMutation = useDetectConflictsMutation();
  const resolveConflictMutation = useResolveConflictMutation();

  const handleDetectConflicts = async () => {
    try {
      const result = await detectConflictsMutation.mutateAsync({
        recitalId,
        data: { forceRecheck: true },
      });

      if (result.conflictsFound === 0) {
        toast.success('No conflicts detected!');
      } else {
        toast.info(`Found ${result.conflictsFound} conflict(s)`);
      }
    } catch (error) {
      toast.error('Failed to detect conflicts');
    }
  };

  const handleResolveConflict = async (conflictId: string) => {
    try {
      await resolveConflictMutation.mutateAsync({ conflictId, recitalId });
      toast.success('Conflict resolved successfully');
    } catch (error) {
      toast.error('Failed to resolve conflict');
      throw error;
    }
  };

  const errorCount =
    conflicts?.filter((c) => c.severity === 'error' && !c.resolved).length || 0;
  const warningCount =
    conflicts?.filter((c) => c.severity === 'warning' && !c.resolved).length ||
    0;
  const resolvedCount = conflicts?.filter((c) => c.resolved).length || 0;

  const renderConflictFilters = (stacked: boolean) => (
    <>
      <div
        className={
          stacked
            ? 'space-y-2'
            : 'flex items-center gap-2'
        }
      >
        {stacked ? (
          <label className='block text-sm font-medium'>Status</label>
        ) : (
          <span className='text-sm font-medium'>Status:</span>
        )}
        <Select
          value={resolvedFilter}
          onValueChange={(v) => {
            setResolvedFilter(v as FilterOption);
          }}
        >
          <SelectTrigger className={stacked ? 'w-full' : 'w-[150px]'}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Conflicts</SelectItem>
            <SelectItem value='unresolved'>Unresolved Only</SelectItem>
            <SelectItem value='resolved'>Accepted Only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div
        className={
          stacked
            ? 'space-y-2'
            : 'flex items-center gap-2'
        }
      >
        {stacked ? (
          <label className='block text-sm font-medium'>Severity</label>
        ) : (
          <span className='text-sm font-medium'>Severity:</span>
        )}
        <Select
          value={severityFilter}
          onValueChange={(v) => {
            setSeverityFilter(v as SeverityFilter);
          }}
        >
          <SelectTrigger className={stacked ? 'w-full' : 'w-[150px]'}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Severities</SelectItem>
            <SelectItem value='error'>Errors Only</SelectItem>
            <SelectItem value='warning'>Warnings Only</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <div>
      <Card>
        <CardHeader>
          <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
            <div className='min-w-0'>
              <CardTitle className='flex flex-wrap items-center gap-2'>
                <AlertTriangle className='size-5 shrink-0' />
                Conflict Management
              </CardTitle>
              <CardDescription>
                Review and resolve scheduling conflicts for performances
              </CardDescription>
            </div>
            <Sheet
              open={conflictActionsOpen}
              onOpenChange={setConflictActionsOpen}
            >
              <SheetTrigger asChild>
                <Button variant='outline' className='relative w-full md:hidden'>
                  <Menu className='mr-2 size-4' />
                  Conflict actions
                  {activeFilterCount > 0 ? (
                    <Badge className='ml-2' variant='secondary'>
                      {activeFilterCount}
                    </Badge>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side='right' className='w-[300px] overflow-y-auto'>
                <SheetHeader>
                  <SheetTitle>Conflict actions</SheetTitle>
                  <SheetDescription>
                    Detect conflicts, accept or resolve them, or change filters.
                  </SheetDescription>
                </SheetHeader>
                <div className='mt-6 flex flex-col gap-3'>
                  {hasAdminAccess ? (
                    <Button
                      variant='outline'
                      className='w-full justify-start'
                      size='sm'
                      onClick={() => {
                        setConflictActionsOpen(false);
                        setRulesDialogOpen(true);
                      }}
                    >
                      <Settings className='mr-2 size-4' />
                      Configure Rules
                    </Button>
                  ) : null}
                  <Button
                    variant='outline'
                    className='w-full justify-start'
                    size='sm'
                    onClick={() => {
                      setConflictActionsOpen(false);
                      void handleDetectConflicts();
                    }}
                    disabled={detectConflictsMutation.isPending}
                  >
                    <RefreshCw
                      className={`mr-2 size-4 ${detectConflictsMutation.isPending ? 'animate-spin' : ''}`}
                    />
                    {detectConflictsMutation.isPending
                      ? 'Detecting...'
                      : 'Detect Conflicts'}
                  </Button>
                  {conflicts && conflicts.length > 0 ? (
                    <Button
                      className='w-full justify-start'
                      size='sm'
                      onClick={() => {
                        setConflictActionsOpen(false);
                        setDrawerOpen(true);
                      }}
                    >
                      <Filter className='mr-2 size-4' />
                      Accept Conflicts
                    </Button>
                  ) : null}
                  <div className='border-t pt-3' />
                  {renderConflictFilters(true)}
                </div>
              </SheetContent>
            </Sheet>
            <div className='hidden shrink-0 flex-wrap gap-2 md:flex'>
              {hasAdminAccess && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setRulesDialogOpen(true);
                  }}
                >
                  <Settings className='mr-2 size-4' />
                  Configure Rules
                </Button>
              )}
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  void handleDetectConflicts();
                }}
                disabled={detectConflictsMutation.isPending}
              >
                <RefreshCw
                  className={`mr-2 size-4 ${detectConflictsMutation.isPending ? 'animate-spin' : ''}`}
                />
                {detectConflictsMutation.isPending
                  ? 'Detecting...'
                  : 'Detect Conflicts'}
              </Button>
              {conflicts && conflicts.length > 0 && (
                <Button
                  variant='default'
                  size='sm'
                  onClick={() => {
                    setDrawerOpen(true);
                  }}
                >
                  <Filter className='mr-2 size-4' />
                  Accept Conflicts
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className='mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4'>
            <div className='rounded-lg border bg-card p-4'>
              <div className='mb-1 text-sm text-muted-foreground'>
                Total Conflicts
              </div>
              <div className='text-3xl font-bold'>{conflicts?.length || 0}</div>
            </div>
            <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
              <div className='mb-1 text-sm text-red-700'>Critical Errors</div>
              <div className='text-3xl font-bold text-red-700'>
                {errorCount}
              </div>
            </div>
            <div className='rounded-lg border border-orange-200 bg-orange-50 p-4'>
              <div className='mb-1 text-sm text-orange-700'>Warnings</div>
              <div className='text-3xl font-bold text-orange-700'>
                {warningCount}
              </div>
            </div>
            <div className='rounded-lg border border-green-200 bg-green-50 p-4'>
              <div className='mb-1 text-sm text-green-700'>
                Accepted Conflicts
              </div>
              <div className='text-3xl font-bold text-green-700'>
                {resolvedCount}
              </div>
            </div>
          </div>

          {/* Severity Info */}
          <Alert className='mb-4'>
            <Info className='size-4' />
            <AlertDescription className='text-xs leading-relaxed'>
              <span className='font-semibold'>Critical Errors</span> are
              blocking conflicts that must be resolved before publishing (e.g.,
              time overlaps, venue conflicts).
              <span className='ml-3 font-semibold'>Warnings</span> are advisory
              conflicts that should be reviewed but don't block publishing
              (e.g., insufficient costume change time).
            </AlertDescription>
          </Alert>

          {/* Filters — desktop only (mobile: Conflict actions sheet) */}
          <div className='mb-6 hidden flex-wrap gap-4 md:flex'>
            {renderConflictFilters(false)}
          </div>

          {/* Conflicts List */}
          {isLoading ? (
            <div className='py-12 text-center'>
              <div className='text-muted-foreground'>Loading conflicts...</div>
            </div>
          ) : !conflicts || conflicts.length === 0 ? (
            <div className='rounded-lg border-2 border-dashed py-12 text-center'>
              <CheckCircle2 className='mx-auto mb-4 size-12 text-green-500' />
              <h3 className='mb-2 text-lg font-semibold'>No Conflicts Found</h3>
              <p className='mb-4 text-muted-foreground'>
                {resolvedFilter === 'unresolved'
                  ? 'All conflicts have been resolved!'
                  : 'Run conflict detection to check for scheduling issues'}
              </p>
              {resolvedFilter === 'all' && (
                <Button onClick={handleDetectConflicts}>
                  <RefreshCw className='mr-2 size-4' />
                  Detect Conflicts
                </Button>
              )}
            </div>
          ) : (
            <div className='space-y-3'>
              {conflicts.map((conflict) => (
                <div
                  key={conflict._id}
                  className={`rounded-lg border p-4 transition-all ${
                    conflict.severity === 'error'
                      ? 'border-red-200 bg-red-50/50'
                      : 'border-orange-200 bg-orange-50/50'
                  } ${conflict.resolved ? 'opacity-60' : ''}`}
                >
                  <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4'>
                    <div className='min-w-0 flex-1'>
                      <div className='mb-2 flex flex-wrap items-center gap-2'>
                        <AlertTriangle
                          className={`size-4 ${
                            conflict.severity === 'error'
                              ? 'text-red-600'
                              : 'text-orange-600'
                          }`}
                        />
                        <h4 className='text-sm font-semibold'>
                          {CONFLICT_LABELS[
                            conflict.conflictType as ConflictType
                          ] ??
                            conflict.conflictType
                              .replace(/_/g, ' ')
                              .toUpperCase()}
                        </h4>
                        <Badge
                          variant='outline'
                          className={
                            conflict.severity === 'error'
                              ? 'border-red-200 bg-red-100 text-red-700'
                              : 'border-orange-200 bg-orange-100 text-orange-700'
                          }
                        >
                          {conflict.severity}
                        </Badge>
                        {conflict.resolved && (
                          <Badge
                            variant='outline'
                            className='border-green-200 bg-green-100 text-green-700'
                          >
                            <CheckCircle2 className='mr-1 size-3' />
                            Resolved
                          </Badge>
                        )}
                      </div>
                      <p className='mb-2 text-sm leading-relaxed text-muted-foreground break-words'>
                        {conflict.description}
                      </p>
                      <div className='flex flex-col gap-1.5 text-xs text-muted-foreground'>
                        <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                          <span className='font-medium text-foreground'>
                            Performance:{' '}
                            {getPerformanceName(conflict.performanceId)}
                          </span>
                          {conflict.relatedPerformanceIds?.length > 0 && (
                            <span>
                              <span className='font-medium'>
                                Conflicts with:{' '}
                              </span>
                              {conflict.relatedPerformanceIds
                                .map((p) => getPerformanceName(p))
                                .join(', ')}
                            </span>
                          )}
                        </div>
                        <div>
                          <span className='font-medium'>Student(s): </span>
                          <span>{getStudentNames(conflict)}</span>
                        </div>
                        {conflict.metadata && (
                          <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                            {conflict.metadata.actualGap !== undefined &&
                              conflict.metadata.requiredGap !== undefined && (
                                <span>
                                  Gap: {formatGap(conflict.metadata.actualGap)}{' '}
                                  (needs {formatGap(conflict.metadata.requiredGap)})
                                </span>
                              )}
                            {conflict.metadata.studentAge !== undefined && (
                              <span>
                                Student age: {conflict.metadata.studentAge}
                              </span>
                            )}
                            {conflict.metadata.performanceCount !==
                              undefined && (
                              <span>
                                Performances:{' '}
                                {conflict.metadata.performanceCount}
                              </span>
                            )}
                            {conflict.metadata.show1 &&
                              conflict.metadata.show2 && (
                                <span>
                                  Shows: {conflict.metadata.show1} &{' '}
                                  {conflict.metadata.show2}
                                </span>
                              )}
                            {conflict.metadata.siblingNames &&
                              conflict.metadata.siblingNames.length > 0 && (
                                <span>
                                  Siblings:{' '}
                                  {conflict.metadata.siblingNames.join(', ')}
                                </span>
                              )}
                            {conflict.metadata.missingField && (
                              <span>
                                Missing: {conflict.metadata.missingField}
                              </span>
                            )}
                          </div>
                        )}
                        {getResolutionGuidance(conflict) && (
                          <div className='mt-1 rounded bg-blue-50 px-2.5 py-1.5 text-xs text-blue-800'>
                            <span className='font-semibold'>Suggestion: </span>
                            {getResolutionGuidance(conflict)}
                          </div>
                        )}
                      </div>
                    </div>
                    {!conflict.resolved ? (
                      <Button
                        size='sm'
                        variant='outline'
                        className='w-full shrink-0 sm:w-auto'
                        onClick={() => {
                          void handleResolveConflict(conflict._id);
                        }}
                        disabled={resolveConflictMutation.isPending}
                      >
                        <CheckCircle2 className='mr-1 size-3' />
                        Accept Conflict
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conflict Resolution Drawer */}
      <ConflictResolutionDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
        conflicts={conflicts || []}
        onResolveConflict={handleResolveConflict}
      />

      {/* Conflict Rules Configuration Dialog */}
      <EditConflictRulesDialog
        open={rulesDialogOpen}
        onOpenChange={setRulesDialogOpen}
        recitalId={recitalId}
      />
    </div>
  );
};
